import React from 'react';
import { useProperties } from '../../../hooks/useProperties';
import { useAgents } from '../../../hooks/useAgents';
import { formatPrice } from '../../../utils/formatters';

interface AgentStatsProps {
  dateRange: [Date, Date];
  selectedTypes: string[];
}

export const AgentStats: React.FC<AgentStatsProps> = ({
  dateRange,
  selectedTypes,
}) => {
  const { properties } = useProperties();
  const { agents } = useAgents();

  // Calculer les statistiques par agent
  const agentStats = agents.map(agent => {
    const agentProperties = properties.filter(property => {
      const inDateRange = property.createdAt >= dateRange[0] && property.createdAt <= dateRange[1];
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(property.type);
      return property.agent === agent.id && inDateRange && matchesType;
    });

    const soldProperties = agentProperties.filter(p => p.status === 'sold');
    const rentedProperties = agentProperties.filter(p => p.status === 'rented');

    const totalSales = soldProperties.reduce((acc, p) => acc + p.price, 0);
    const totalCommissions = soldProperties.reduce((acc, p) => acc + p.commission, 0);

    return {
      agent,
      totalProperties: agentProperties.length,
      soldCount: soldProperties.length,
      rentedCount: rentedProperties.length,
      totalSales,
      totalCommissions,
    };
  });

  // Trier par nombre total de transactions
  agentStats.sort((a, b) => (b.soldCount + b.rentedCount) - (a.soldCount + a.rentedCount));

  return (
    <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Performance des commerciaux
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Commercial
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Biens en portefeuille
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Ventes
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Locations
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                CA Ventes
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Commissions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {agentStats.map(({ agent, totalProperties, soldCount, rentedCount, totalSales, totalCommissions }) => (
              <tr key={agent.id}>
                <td className="whitespace-nowrap px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{agent.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{agent.title}</div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {totalProperties}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {soldCount}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {rentedCount}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {Math.floor(totalSales).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {Math.floor(totalCommissions).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};